import "./modal.css"

import EventEmitter from "eventemitter3";
import { EventTypes } from "../../core/event-bus";
import { Context } from "../../models/context";
import { Modals } from '../../types/enums';
import { LocalizationService } from "../../services/localization-service";
import { DataLayerService } from '../../services/data-layer-service';

interface IModalProps {
    hasCloseButton?: boolean;
    onClose?: () => void;
    hasCloseOnBlur?: boolean;
    disableEscapeClose?: boolean;
    hasDimmer?: boolean;
    disableDimmer?: boolean;
    header?: IModalHeader;
    footer?: string;
    ariaLabel: string
}

interface IModalHeader {
    title?: string;
    content?: string;
}

export abstract class Modal {
    protected readonly _elementId: string;
    protected _eventBus: EventEmitter;
    protected _context: Context;
    protected _localizationService: LocalizationService;
    protected _dataLayer: DataLayerService;
    protected _element: HTMLDialogElement | null = null;
    protected readonly _elementDelete: HTMLElement | null = null;
    protected readonly _elementHeader: HTMLElement | null = null;
    protected readonly _elementFooter: HTMLElement | null = null;
    protected readonly _props: IModalProps;
    private _contentTag: Modals | null;
    private _isOpen = false;
    private _triggerElement: HTMLElement | null = null;

    constructor(elementId: string, eventBus: EventEmitter, context: Context, dataLayer: DataLayerService, localizationService: LocalizationService, props?: IModalProps, contentTag?: Modals) {
        this._elementId = elementId;
        this._eventBus = eventBus;
        this._context = context;
        this._dataLayer = dataLayer;
        this._localizationService = localizationService;
        this._props = { hasDimmer: true, disableDimmer: false, ariaLabel: "", ...props };
        this._props.hasCloseButton = !!props?.hasCloseButton;
        this._props.hasCloseOnBlur = !!props?.hasCloseOnBlur;
        this._props.disableEscapeClose = props?.disableEscapeClose === true;
        this._contentTag = contentTag || null;
        this._element = document.getElementById(this._elementId) as HTMLDialogElement;
        this._elementDelete = document.getElementById(this._elementId + "_delete");
        this._elementHeader = document.querySelector(`#${this._elementId} .modal-header`);
        this._elementFooter = document.querySelector(`#${this._elementId} .modal-footer`);

        if (this._element)
            this._element.inert = true;

        this.open = this.open.bind(this);
        this.close = this.close.bind(this);
    }

    public get id(): string {
        return this._elementId;
    }

    protected _setAriaLabelledBy(elementId: string): void {
        this._element?.setAttribute("aria-labelledby", elementId);
    }

    public set title(header: string) {
        if (this._props.header)
            this._props.header.title = header;
    }

    public init(): void {
        if (!this._props?.hasCloseButton && this._elementDelete) {
            this._elementDelete.style.display = "none";
        }

        if (this._props?.header && this._elementHeader) {
            const header = this._props.header;
            const elementHeaderTitle = this._elementHeader.querySelector(".header-title");
            if (elementHeaderTitle && header.title) elementHeaderTitle.textContent = header.title;

            const elementHeaderContent = this._elementHeader.querySelector(".content");
            if (elementHeaderContent && header?.content) elementHeaderContent.innerHTML = header.content;

            this._elementHeader.classList.remove("hidden")
        }

        // Fallback: dialogs without a visible header title need aria-label directly on <dialog>.
        // Skip when aria-labelledby is already present in markup (e.g. OTAT uses an in-dialog <h2>).
        if (!this._props?.header?.title && !this._element?.hasAttribute("aria-labelledby"))
            this._element?.setAttribute("aria-label", this._props?.ariaLabel ?? "Dialog");

        if (this._props?.footer && this._elementFooter) {
            this._elementFooter.innerHTML = this._props?.footer;
            this._elementFooter.classList.remove("hidden")
        }

        // Block the native close request so browsers that ignore closedby="none" can't half-open the dialog and leak focus to the page.
        this._element?.addEventListener("cancel", (e) => e.preventDefault());

        // Escape closes the modal and returns focus to the trigger (WCAG 2.1.2); opt out via disableEscapeClose for must-acknowledge modals.
        // Always preventDefault on Escape so the browser never fires the native `cancel` event (which can strip the ::backdrop even when cancel is prevented).
        this._element?.addEventListener("keydown", (e) => {
            if (e.key === "Escape" && this._isOpen) {
                e.preventDefault();
                if (!this._props.disableEscapeClose)
                    this._onCloseClick();
            }
        });

        // Backdrop (light-dismiss) close, opt-in via hasCloseOnBlur; a click targeting the dialog element itself lands on the backdrop, not inner content.
        this._element?.addEventListener("click", (e) => {
            if (this._props.hasCloseOnBlur && e.target === this._element)
                this._onCloseClick();
        });
    }

    public open(): void {
        this._isOpen = true;

        // Remember what had focus (e.g. the menu item card) so we can return focus to it on close (WCAG 2.4.3).
        this._triggerElement = document.activeElement as HTMLElement | null;

        const dialog = this._element as HTMLDialogElement;
        if (dialog)
            dialog.inert = false;

        dialog?.classList.toggle("open", true);
        dialog?.showModal();

        // Prevent multiple listeners
        this._elementDelete?.removeEventListener("click", this._onCloseClick);
        if (this._props?.hasCloseButton)
            this._elementDelete?.addEventListener("click", this._onCloseClick);

        this._element?.addEventListener('touchmove', this._stopTouchMove, { passive: false });
        document.body.addEventListener('touchmove', this._preventTouchMove, { passive: false });
    }

    public close(): void {
        // Ignore accidental cross-instance calls
        if (!this._isOpen)
            return; 

        this._isOpen = false;

        // Clear inputs
        [...(Array.from(this._element?.getElementsByTagName("input") || []))]?.forEach((e: HTMLInputElement) => {
            switch (e.type) {
                case "checkbox":
                    e.checked = false;
                    break;
                default:
                    e.value = "";
            }

            e.dispatchEvent(new Event("input"))
        })

        const dialog = this._element as HTMLDialogElement;
        dialog?.classList.toggle("open", false);
        dialog?.close();
        if (dialog)
            dialog.inert = true;

        this._element?.removeEventListener('touchmove', this._stopTouchMove);
        document.body.removeEventListener('touchmove', this._preventTouchMove);

        // Return focus to whatever opened the modal so keyboard/screen-reader users land back where they were (WCAG 2.4.3).
        if (this._triggerElement?.isConnected)
            this._triggerElement.focus();
        this._triggerElement = null;

        this._eventBus.emit(EventTypes.MODAL_CLOSED);
        this._props?.onClose?.();
    }

    private readonly _onCloseClick = (): void => {
        this.close();

        if (this._dataLayer && this._contentTag)
            this._dataLayer.modal_close(this._contentTag);
    };

    private _stopTouchMove = (e: TouchEvent) => {
        e.stopPropagation();
    };

    private _preventTouchMove = (e: TouchEvent) => {
        e.preventDefault();
    };

    protected abstract _setEventListeners(): void;
}